import { useColorModeValue, useToast } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import useDebounce from '@/hooks/useDebounce';
import { Store } from '@/pages/camel-kiosk/accounts/index/components/AccountTable';
import { FilterType } from '@/pages/camel-kiosk/accounts/index/components/FilterOptions';
import { routes } from '@/router';
import { deleteStore, getStoreList } from '@/services/Kiosk';

export const LIST_QUERY_KEY = 'kiosk-stores-list';

export const useCamelKioskAccountsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState<FilterType>('all');
  const queryClient = useQueryClient();
  const history = useHistory();
  const itemsPerPage = 30;

  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  const {
    data,
    error: getError,
    isLoading: getIsLoading,
  } = useQuery([LIST_QUERY_KEY, currentPage, debouncedSearchTerm], () =>
    getStoreList({ limit: itemsPerPage, currentPage, name: searchTerm })
  );

  const stores = data?.master_stores.map(
    (store): Store => ({
      id: `${store.master_store_id}`,
      accountId: store.kiosk_account?.id ?? null,
      name: store.name,
      loginId: store.kiosk_account?.login_id ?? null,
      isConfigured: store.kiosk_account != null,
    })
  );

  // フィルタリングとページネーションを適用したデータ
  const filteredStores = useMemo(() => {
    let result = [...(stores || [])];

    // 状態フィルタリング
    if (filterType === 'configured') {
      result = result.filter((store) => store.isConfigured);
    } else if (filterType === 'unconfigured') {
      result = result.filter((store) => !store.isConfigured);
    }

    return result;
  }, [stores, filterType]);

  const totalItems = data?.count ?? 0;

  const toast = useToast();

  // ハンドラー関数
  const handlePasswordChange = useCallback(
    (storeId: string, name: string, kioskAccountId: string) => {
      history.push(
        routes.CamelKioskPasswordChange.path.replace(':id', storeId) +
          `?name=${name}&kioskAccountId=${kioskAccountId}`
      );
    },
    []
  );
  const handleConfigure = useCallback((accountId: string) => {
    history.push(routes.CamelKioskStore.path.replace(':id', accountId));
  }, []);
  const handleDelete = useCallback(
    async (accountId: string) => {
      await deleteStore({ accountId });
      queryClient.invalidateQueries([LIST_QUERY_KEY]);
      toast({
        title: 'アカウントを削除しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    [toast, deleteStore]
  );
  const onCreateAccount = useCallback((masterStoreId: string, name: string) => {
    history.push(
      routes.CamelKioskAccountCreate.path.replace(':id', masterStoreId) +
        `?name=${name}`
    );
  }, []);
  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);
  const handleFilterChange = useCallback((newFilter: FilterType) => {
    setFilterType(newFilter);
    setCurrentPage(1); // フィルター変更時は1ページ目に戻る
  }, []);

  const handlers = {
    handlePasswordChange,
    handleConfigure,
    handleDelete,
    onCreateAccount,
    handlePageChange,
    handleFilterChange,
  };

  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const error = useMemo(() => {
    if (getError instanceof Error) return getError;
    return null;
  }, [getError]);

  return {
    stores: filteredStores,
    isLoading: getIsLoading,
    error,
    handlers,
    searchTerm,
    setSearchTerm,
    filterType,
    currentPage,
    borderColor,
    totalItems,
    itemsPerPage,
  };
};
